import {
  useGetRepositoryConfig,
  useGetRepositoryModules,
  useResetRepositoryConfig,
  useUpdateRepositoryConfig
} from "@/api/useInventory"
import { App } from "@/atoms"
import { isEmpty } from "@/helpers/utils"
import { useTranslation } from "@/hooks/useTranslation"
import { ROOT_KEY } from "@/molecules/ModulesTree/ModulesTree.types"
import { MonorepoConfigurations } from "@/organisms/MonorepoConfigurations/MonorepoConfigurations"
import { Loader } from "@/pages/SettingsPage/components/molecules/Loader"
import { useBranchEntity } from "../../applications/inventory"

import styles from "./ConfigTab.module.scss"

import type { RepositoryCorrelation } from "@/api/useInventory.types"
import type { Configs } from "@/organisms/MonorepoConfigurations/MonorepoConfigurations.types"
import type {
  CallbacksProps,
  CodeRepoConfigTabComponent
} from "./ConfigTab.types"

const findNestedPaths = (
  paths: string[]
): { parent: string; nested: string }[] => {
  const sortedPaths = [...paths].sort()
  const nestedPairs: { parent: string; nested: string }[] = []
  for (let i = 0; i < sortedPaths.length - 1; i++) {
    const current = sortedPaths[i]
    const next = sortedPaths[i + 1]

    if (next.startsWith(current + "/")) {
      nestedPairs.push({ parent: current, nested: next })
    }
  }

  return nestedPairs
}

const haveDuplicateImages = (configs: Configs) => {
  const images = Object.values(configs).flatMap((config) =>
    config.imageRepositories.map((i) => i.imageRepository)
  )
  return new Set(images).size !== images.length
}

const defaultEmptyConfig = (correlations: RepositoryCorrelation[]) => {
  return {
    "": {
      projectName: "",
      splitPath: false,
      exclude: false,
      imageRepositories: correlations
        .filter((c) => c.path === "")
        .map((c) => ({
          imageRepository: c.imageRepository,
          correlationType: c.source
        }))
    }
  } as Configs
}

export const CodeRepoConfigTab: CodeRepoConfigTabComponent = (props) => {
  const { message } = App.useApp()
  // we use related images to overcome coupling between project configs and custom correlations
  const [context, { repositoryName, sourceControl }] = useBranchEntity(
    props.record?.id
  )

  const { t } = useTranslation()
  // Address every config endpoint by the stable code-repo id from the repo row, so a rename can't misroute.
  const record = context.response?.data?.[0]
  const repositoryConfig = useGetRepositoryConfig(repositoryName, record)
  const modules = useGetRepositoryModules(repositoryName, record)
  const updateConfig = useUpdateRepositoryConfig(repositoryName, record)
  const resetConfig = useResetRepositoryConfig(repositoryName, record)

  if (modules.isLoading || context.isLoading) return <Loader />

  const isError = modules.isError || repositoryConfig.isError || context.isError

  if (isError)
    return (
      <div className={styles.error}>
        {t("inventory.codeRepositoriesTab.drawer.config.errors.failedLoading")}
      </div>
    )

  const relatedImages = repositoryConfig.response?.correlations || []
  const isEmptyConfig = isEmpty(repositoryConfig.response?.config)

  return (
    <div className={styles.container}>
      <MonorepoConfigurations
        record={record}
        configLoading={repositoryConfig.isLoading}
        repoName={repositoryName}
        scm={sourceControl}
        modules={modules.response || []}
        isEmptyConfig={isEmptyConfig}
        repoPathsVM={repositoryConfig.response?.repoPathsVM}
        configs={
          isEmptyConfig
            ? defaultEmptyConfig(relatedImages)
            : repositoryConfig.response!.config
        }
        loading={updateConfig.isPending}
        onReset={async () => {
          try {
            await resetConfig.mutateAsync(undefined)
            await repositoryConfig.refetch()
          } catch (err: unknown) {
            return Promise.reject(err)
          }
        }}
        onSave={async (config, props?: CallbacksProps) => {
          const nestedPaths = findNestedPaths(Object.keys(config))
          const hasNestedPaths = nestedPaths.length > 0

          if (hasNestedPaths) {
            message.error(
              t(
                "inventory.codeRepositoriesTab.drawer.config.errors.nestedPaths",
                {
                  parent: nestedPaths[0].parent,
                  child: nestedPaths[0].nested
                }
              )
            )

            if (props?.noMessage) return Promise.reject()
            return
          }

          if (haveDuplicateImages(config)) {
            message.error(
              t(
                "inventory.codeRepositoriesTab.drawer.config.errors.duplicateImages"
              )
            )
            return
          }

          try {
            // Recover each project's stable id by path (a rename changes the name, not the path) from
            // the loaded config. Count occurrences so a project this path-keyed view split across
            // several paths is not re-sent with the same id, which would create duplicate ids.
            const loadedConfig = repositoryConfig.response?.config || {}
            const idPathCount = Object.values(loadedConfig).reduce<
              Record<string, number>
            >((acc, entry) => {
              if (entry.id) acc[entry.id] = (acc[entry.id] || 0) + 1
              return acc
            }, {})

            await updateConfig.mutateAsync({
              projects: Object.keys(config).map((path) => {
                const project = {
                  name: config[path].projectName,
                  paths: [path === ROOT_KEY ? "" : "/" + path],
                  ignore: config[path].exclude,
                  imageRepositories: config[path].imageRepositories.map(
                    (i) => i.imageRepository
                  ),
                  split: config[path].splitPath
                }
                const id = loadedConfig[path]?.id
                return id && idPathCount[id] === 1
                  ? { ...project, id }
                  : project
              })
            })

            if (!props?.noMessage)
              message.success(
                t("inventory.codeRepositoriesTab.drawer.config.saveSuccess")
              )

            repositoryConfig.refetch()
          } catch (err: unknown) {
            if (!props?.noMessage)
              message.error(
                t(
                  "inventory.codeRepositoriesTab.drawer.config.errors.saveFailed"
                )
              )
            else return Promise.reject(err)
          }
        }}
      />
    </div>
  )
}
